import React, { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { GalleryItems, Testimonials } from "./constants";

const GalleryAndTestimonials = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);



  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentGalleryIndex((prev) => (prev + 1) % GalleryItems.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [GalleryItems.length]);

  const nextGallerySlide = () => {
    setCurrentGalleryIndex((prev) => (prev + 1) % GalleryItems.length);
  };

  const prevGallerySlide = () => {
    setCurrentGalleryIndex(
      (prev) => (prev - 1 + GalleryItems.length) % GalleryItems.length
    );
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % Testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + Testimonials.length) % Testimonials.length
    );
  };

  return (
    <>
      {/* Gallery Section */}
      <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-20 sm:py-24 lg:py-32 overflow-hidden">
        {/* Glow effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          {/* Header */}
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Our Work{" "}
              <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                Speaks for Itself
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              Browse our portfolio of completed projects showcasing quality
              craftsmanship and attention to detail
            </p>
          </div>

          {/* Gallery Slider */}
          <div className="mt-16 relative max-w-5xl mx-auto">
            {/* Main slide */}
            <div className="relative overflow-hidden rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-orange-500/50 transition-all duration-300">
              {/* Image */}
              <div
                className="relative h-96 sm:h-[500px] overflow-hidden cursor-pointer"
                onClick={() =>
                  setSelectedImage(GalleryItems[currentGalleryIndex])
                }
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-orange-600/0 hover:from-orange-500/20 hover:to-orange-600/10 transition-all duration-300 z-10" />
                <img
                  src={GalleryItems[currentGalleryIndex].image}
                  alt={GalleryItems[currentGalleryIndex].title}
                  className="h-full w-full object-cover"
                />
                {/* Category badge */}
                <div className="absolute top-6 right-6 z-20 bg-orange-500/90 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-full">
                  {GalleryItems[currentGalleryIndex].category}
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-3">
                  {GalleryItems[currentGalleryIndex].title}
                </h3>
                <p className="text-slate-400">
                  {GalleryItems[currentGalleryIndex].description}
                </p>
              </div>
            </div>

            {/* Navigation buttons */}
            <button
              onClick={prevGallerySlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-900/80 backdrop-blur-sm border border-slate-700 text-white hover:bg-slate-800 hover:border-orange-500/50 transition-all shadow-xl z-20"
              aria-label="Previous project"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextGallerySlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-900/80 backdrop-blur-sm border border-slate-700 text-white hover:bg-slate-800 hover:border-orange-500/50 transition-all shadow-xl z-20"
              aria-label="Next project"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {GalleryItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGalleryIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentGalleryIndex
                      ? "w-8 bg-orange-500"
                      : "w-2 bg-slate-600 hover:bg-slate-500"
                  }`}
                  aria-label={`Go to project ${index + 1}`}
                />
              ))}
            </div>

            {/* Thumbnail strip */}
            <div className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-3">
              {GalleryItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setCurrentGalleryIndex(index)}
                  className={`relative overflow-hidden rounded-lg aspect-video transition-all ${
                    index === currentGalleryIndex
                      ? "ring-2 ring-orange-500 opacity-100"
                      : "opacity-50 hover:opacity-100"
                  }`}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-20 sm:py-24 lg:py-32 overflow-hidden">
        {/* Glow effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
          <div className="absolute bottom-1/3 right-1/2 translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          {/* Header */}
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              What Our Customers{" "}
              <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                Are Saying
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              Don't just take our word for it - hear from our satisfied
              customers
            </p>
          </div>

          {/* Featured Testimonial Carousel */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-12">
              {/* Quote icon */}
              <Quote className="absolute top-8 left-8 h-12 w-12 text-orange-500/20" />

              <div className="relative">
                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(Testimonials[currentTestimonial].rating)].map(
                    (_, i) => (
                      <Star
                        key={i}
                        className="h-6 w-6 fill-orange-400 text-orange-400"
                      />
                    )
                  )}
                </div>

                {/* Testimonial text */}
                <p className="text-xl text-slate-200 text-center mb-8 leading-relaxed">
                  "{Testimonials[currentTestimonial].text}"
                </p>

                {/* Customer info */}
                <div className="text-center">
                  <p className="text-lg font-semibold text-white">
                    {Testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-sm text-slate-400">
                    {Testimonials[currentTestimonial].location} •{" "}
                    {Testimonials[currentTestimonial].date}
                  </p>
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={prevTestimonial}
                  className="p-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white hover:bg-slate-700 hover:border-orange-500/50 transition-all"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="p-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white hover:bg-slate-700 hover:border-orange-500/50 transition-all"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-6">
                {Testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? "w-8 bg-orange-500"
                        : "w-2 bg-slate-600 hover:bg-slate-500"
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Testimonial Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Testimonials.slice(0, 3).map((testimonial, index) => (
              <div
                key={testimonial.id}
                style={{ animationDelay: `${index * 0.1}s` }}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-orange-500/50 transition-all fade-in-up"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-orange-400 text-orange-400"
                    />
                  ))}
                </div>

                {/* Text */}
                <p className="text-slate-300 mb-4 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Customer info */}
                <div className="border-t border-slate-700 pt-4">
                  <p className="font-semibold text-white text-sm">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {testimonial.location}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            {/* <p className="text-slate-300 mb-6">
              Ready to experience the same quality service?
            </p> */}
            {/* <a
              href="request-appointment"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
            >
              temp comment
              Get Your Free Quote
            </a> */}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-orange-400 transition-colors"
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">
                {selectedImage.title}
              </h3>
              <p className="text-slate-300 mt-2">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default GalleryAndTestimonials;
